import React, { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { PageTitle } from '../../../_metronic/layout/core';
import TopBar from './sections/TopBar';
import KPIStrip from './sections/KPIStrip';
import AttentionQueue from './sections/AttentionQueue';
import TodayTimeline from './sections/TodayTimeline';
import EventLog from './sections/EventLog';
import AdsMonitorsCard from './sections/AdsMonitorsCard';
import AndreiTasksCard from './sections/AndreiTasksCard';
import ActiveCustomerList from '../dashboard/ActiveCustomerList';
import ChangeActivityPanel from '../marketing/ChangeActivityPanel';
import { InvoiceProvider } from '../invoices/contexts/InvoiceContext';
import { BillsProvider } from '../bills/contexts/BillsContext';
// ChangeActivityPanel's ads-portfolio__* styles live in the marketing page
// bundle — import it here too so the feed is styled before that page loads.
import '../marketing/GoogleAdsPage.scss';
import './HomePage.scss';

/**
 * HomePage — the dashboard rendered at /dashboard when the Terminal theme
 * is active (wired via DashboardWrapper). Priority-scored Attention Queue
 * is the main surface ("what needs my attention right now?"), with a Today
 * timeline to the right and the existing customer/job card grid below
 * (restyled in place via scoped SCSS overrides to match the terminal aesthetic).
 *
 * When the Terminal theme is NOT active, DashboardWrapper renders the
 * classic UserDashboard instead and this component doesn't mount.
 *
 * Wrapped in InvoiceProvider + BillsProvider so:
 *   1. useAttentionItems / KPIStrip can source native Invoice/Bill shapes
 *   2. InvoiceViewModal + BillViewModal (opened inline from attention rows)
 *      find the contexts they require. JobWorkspaceModal brings its own
 *      InvoiceProvider internally, but nesting is cheap and idempotent.
 */
const HomePage: React.FC = () => {
  const navigate = useNavigate();
  // The approvals feed + monitors card both deep-link into the Google Ads
  // page with the account preselected (GoogleAdsPage honours ?cid=).
  const openAccount = useCallback((cid: string) => {
    navigate(`/marketing/google-ads?cid=${encodeURIComponent(cid)}`);
  }, [navigate]);

  return (
    <InvoiceProvider>
      <BillsProvider>
        <div className="home-root">
          <PageTitle breadcrumbs={[]}>Dashboard · new</PageTitle>

          <TopBar />

          <KPIStrip />

          <div className="home-main-grid">
            <section className="home-attention">
              <AttentionQueue />
            </section>
            <aside className="home-rail">
              <TodayTimeline />
              <EventLog />
            </aside>
          </div>

          {/* Universal approval feed (Wave 3 P1): every pending Google Ads
              proposal — Andrei drafts, monitor drafts, manual — approvable
              right here, plus the autonomous-monitor management card. Inbox
              mode shows only needs-attention rows, so this section stays
              quiet when there's nothing to decide. */}
          <section className="home-andrei-ops">
            <div className="home-section-divider">
              <span className="home-section-label">## APPROVALS &amp; MONITORS ##</span>
              <span className="home-section-dashes">─────────────────────────────────────────────────────────────────</span>
            </div>
            <div className="home-andrei-ops__grid">
              <div className="home-andrei-ops__feed">
                <ChangeActivityPanel onSelectAccount={openAccount} />
              </div>
              <AdsMonitorsCard onSelectAccount={openAccount} />
              <AndreiTasksCard />
            </div>
          </section>

          <section className="home-customers">
            <div className="home-section-divider">
              <span className="home-section-label">## ACTIVE CUSTOMERS ##</span>
              <span className="home-section-dashes">─────────────────────────────────────────────────────────────────</span>
            </div>
            <ActiveCustomerList />
          </section>
        </div>
      </BillsProvider>
    </InvoiceProvider>
  );
};

export default HomePage;
